import { type Graph, type GraphEdge, edgesFrom, edgesOf, nodeById } from '../models/graph';

export function dijkstra(
  graph: Graph,
  sourceId: string,
  targetId: string,
  oneWayEdgeIds: ReadonlySet<string> = new Set(),
): string[] {
  if (sourceId === targetId) return [];
  if (!nodeById(graph, sourceId) || !nodeById(graph, targetId)) return [];

  const dist = new Map<string, number>();
  const prevEdge = new Map<string, GraphEdge>();
  const visited = new Set<string>();

  for (const node of graph.nodes) dist.set(node.id, Infinity);
  dist.set(sourceId, 0);

  const pq: Array<{ nodeId: string; distance: number }> = [{ nodeId: sourceId, distance: 0 }];

  while (pq.length > 0) {
    pq.sort((a, b) => a.distance - b.distance);
    const current = pq.shift()!;
    const currentId = current.nodeId;

    if (visited.has(currentId)) continue;
    visited.add(currentId);
    if (currentId === targetId) break;

    const candidateEdges = graph.directed ? edgesFrom(graph, currentId) : edgesOf(graph, currentId);

    for (const edge of candidateEdges) {
      if (oneWayEdgeIds.has(edge.id) && edge.sourceId !== currentId) continue;

      const neighborId = edge.sourceId === currentId ? edge.targetId : edge.sourceId;
      if (visited.has(neighborId)) continue;

      const newDist = dist.get(currentId)! + edge.weight;
      if (newDist < (dist.get(neighborId) ?? Infinity)) {
        dist.set(neighborId, newDist);
        prevEdge.set(neighborId, edge);
        pq.push({ nodeId: neighborId, distance: newDist });
      }
    }
  }

  if (!prevEdge.has(targetId)) return [];

  const edgePath: string[] = [];
  let current = targetId;
  while (current !== sourceId) {
    const edge = prevEdge.get(current);
    if (!edge) return [];
    edgePath.push(edge.id);
    current = edge.sourceId === current ? edge.targetId : edge.sourceId;
  }

  return edgePath.reverse();
}

export function shortestPathLength(
  graph: Graph,
  sourceId: string,
  targetId: string,
  oneWayEdgeIds: ReadonlySet<string> = new Set(),
): number {
  if (sourceId === targetId) return 0;
  const path = dijkstra(graph, sourceId, targetId, oneWayEdgeIds);
  if (path.length === 0) return Infinity;
  return path.reduce((sum, id) => sum + (graph.edges.find((e) => e.id === id)?.weight ?? 0), 0);
}
